import { Button, ErrorBoundary, Stack, Text } from '@ltvco/refresh-lib/theme';
import { ReportSection } from '@ltvco/refresh-lib/v2';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { QADisplayModal } from './QADisplayModal/QADisplayModal';
import { useAppConfigContext } from '@ltvco/refresh-lib/ctx';
import {
  COMMUNITY_BUZZ_GA4_EVENTS,
  COMMUNITY_BUZZ_INTRO_MODAL_SESSION_KEY,
  QUESTIONS_PER_PAGE,
} from './constants';
import { AnswerFormData, QAEntryModal } from './QAEntryModal/QAEntryModal';
import { useCommunityBuzz } from 'hooks/useCommunityBuzz';
import { Question } from 'api/requests/communityBuzz/interfaces';
import { QuestionsListContent } from './QuestionsListContent';
import { enqueueSnackbar } from '@ltvco/refresh-lib/v1';
import { CommunityBuzzIntroCTA } from './CTAs/CommunityBuzzIntroCTA/CommunityBuzzIntroCTA';
import { CommunityBuzzIntroModal } from './CTAs/CommunityBuzzIntroModal/CommunityBuzzIntroModal';

interface CommunityBuzzProps {
  make: string;
  model: string;
  year: string;
  trim: string;
  limitIntroModalToSession?: boolean;
}

export const CommunityBuzz = ({
  make,
  model,
  year,
  trim,
  limitIntroModalToSession = true,
}: CommunityBuzzProps) => {
  const questionsFetchLimit = QUESTIONS_PER_PAGE;
  const { trackEventGA4, logError } = useAppConfigContext();
  const [openQADisplayModal, setOpenQADisplayModal] = useState(false);
  const [openQAEntryQuestionModal, setOpenQAEntryQuestionModal] =
    useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<
    Question | undefined
  >(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [autoSeedAttempted, setAutoSeedAttempted] = useState(false);
  const [errorRecoveryAttempted, setErrorRecoveryAttempted] = useState(false);
  const [isCommunityBuzzSeeding, setIsCommunityBuzzSeeding] = useState(false);
  const [isIntroModalOpen, setIsIntroModalOpen] = useState(false);
  const {
    questionsListQuery,
    questionQuery,
    createQuestionMutation,
    createAnswerMutation,
    llmGenerationMutation,
    llmTokenReady: communityBuzzLLMTokenReady,
    bootstrapMutation,
    bootstrapTokenReady: communityBuzzOramTokenReady,
  } = useCommunityBuzz({
    questionsListParams: {
      year,
      make,
      model,
      trim,
      page: currentPage,
      page_size: questionsFetchLimit,
    },
    questionId: selectedQuestion?.id,
    llmParams: {
      year,
      make,
      model,
      trim,
    },
  });
  const {
    data: questionsData,
    isLoading: isQuestionsListLoading,
    isError: isQuestionsListError,
    refetch: refetchQuestionsList,
  } = questionsListQuery;
  const {
    data: questionData,
    isLoading: isQuestionLoading,
    isFetching: isQuestionFetching,
  } = questionQuery;
  const isQuestionDetailsLoading = isQuestionLoading || isQuestionFetching;
  const questions = questionsData?.items || [];
  const questionsTotal = useMemo(() => {
    if (typeof questionsData?.total === 'number') {
      return questionsData.total;
    }
    const serverPage = questionsData?.page ?? 1;
    if (serverPage > 1) {
      return (serverPage - 1) * questionsFetchLimit + questions.length;
    }
    return questions.length;
  }, [
    questionsData?.page,
    questionsData?.total,
    questions.length,
    questionsFetchLimit,
  ]);
  const hasExistingQuestions = questionsTotal > 0;
  const isQuestionsResolved =
    !isQuestionsListLoading && !isQuestionsListError && questionsTotal >= 0;
  const isQuestionsResolvedEmpty = isQuestionsResolved && questionsTotal === 0;

  useEffect(() => {
    setCurrentPage(1);
    setAutoSeedAttempted(false);
    setErrorRecoveryAttempted(false);
  }, [year, make, model, trim]);
  const { mutateAsync: runCommunityBuzzLLM } = llmGenerationMutation;
  const { mutateAsync: bootstrapCommunityBuzzQuestions } = bootstrapMutation;
  const hasCommunityBuzzParams = Boolean(year && make && model && trim);
  const canSeedCommunityBuzz =
    hasCommunityBuzzParams &&
    communityBuzzLLMTokenReady &&
    communityBuzzOramTokenReady;
  const askQuestionDisabled =
    isQuestionsListLoading || !communityBuzzOramTokenReady;
  const shouldLimitIntroModalToSession = limitIntroModalToSession;

  const setIntroModalSeen = useCallback(() => {
    if (!shouldLimitIntroModalToSession) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.sessionStorage.setItem(
        COMMUNITY_BUZZ_INTRO_MODAL_SESSION_KEY,
        'true',
      );
    } catch {
      return;
    }
  }, [shouldLimitIntroModalToSession]);

  const hasIntroModalBeenSeen = useCallback(() => {
    if (!shouldLimitIntroModalToSession) {
      return false;
    }

    if (typeof window === 'undefined') {
      return true;
    }

    try {
      return (
        window.sessionStorage.getItem(
          COMMUNITY_BUZZ_INTRO_MODAL_SESSION_KEY,
        ) === 'true'
      );
    } catch {
      return true;
    }
  }, [shouldLimitIntroModalToSession]);

  const seedCommunityBuzzFromLLM = useCallback(
    async (options?: { silent?: boolean; force?: boolean }) => {
      const allowSeed = options?.force || isQuestionsResolvedEmpty;

      if (!allowSeed) {
        if (!options?.silent) {
          enqueueSnackbar('Community Buzz already available.', {
            variant: 'info',
          });
        }
        return Promise.resolve(null);
      }

      if (!canSeedCommunityBuzz) {
        if (!options?.silent) {
          enqueueSnackbar(
            'Community Buzz is not ready yet. Try again shortly.',
            {
              variant: 'warning',
            },
          );
        }
        return Promise.resolve(null);
      }

      setIsCommunityBuzzSeeding(true);

      try {
        try {
          const llmResult = await runCommunityBuzzLLM();
          const llmQuestions = llmResult.data?.questions || [];

          if (!llmQuestions.length) {
            throw new Error('No LLM questions available for Community Buzz');
          }

          const payloadQuestions = llmQuestions.map((item) => ({
            question: item.question,
            answers: item.answer ? [item.answer] : [],
          }));
          await bootstrapCommunityBuzzQuestions({
            questions: payloadQuestions,
          });
          setCurrentPage(1);
          await refetchQuestionsList();
          if (!options?.silent) {
            enqueueSnackbar('Community Buzz updated', {
              variant: 'success',
            });
          }
        } catch (error) {
          logError?.('Failed to update community buzz content', error as Error);

          if (!options?.silent) {
            enqueueSnackbar('Unable to update Community Buzz', {
              variant: 'error',
            });
          }
          return await Promise.reject(error);
        }
      } finally {
        setIsCommunityBuzzSeeding(false);
      }
    },
    [
      isQuestionsResolvedEmpty,
      bootstrapCommunityBuzzQuestions,
      canSeedCommunityBuzz,
      logError,
      runCommunityBuzzLLM,
      refetchQuestionsList,
    ],
  );

  useEffect(() => {
    if (
      !autoSeedAttempted &&
      isQuestionsResolvedEmpty &&
      canSeedCommunityBuzz &&
      !isCommunityBuzzSeeding
    ) {
      setAutoSeedAttempted(true);
      seedCommunityBuzzFromLLM({ silent: true }).catch(() => null);
    }
  }, [
    autoSeedAttempted,
    isQuestionsResolvedEmpty,
    canSeedCommunityBuzz,
    isCommunityBuzzSeeding,
    seedCommunityBuzzFromLLM,
  ]);

  useEffect(() => {
    if (
      isQuestionsListError &&
      !hasExistingQuestions &&
      !isQuestionsListLoading &&
      !isCommunityBuzzSeeding &&
      canSeedCommunityBuzz &&
      !errorRecoveryAttempted
    ) {
      setErrorRecoveryAttempted(true);
      seedCommunityBuzzFromLLM({ silent: true, force: true }).catch(() => null);
    }
  }, [
    canSeedCommunityBuzz,
    errorRecoveryAttempted,
    hasExistingQuestions,
    isCommunityBuzzSeeding,
    isQuestionsListError,
    isQuestionsListLoading,
    seedCommunityBuzzFromLLM,
  ]);

  useEffect(() => {
    if (!isQuestionsListError && errorRecoveryAttempted) {
      setErrorRecoveryAttempted(false);
    }
  }, [errorRecoveryAttempted, isQuestionsListError]);

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleOpenQADisplayModal = () => {
    setOpenQADisplayModal(true);
  };

  const handleCloseQADisplayModal = () => {
    setOpenQADisplayModal(false);
  };

  const handleOpenQAEntryQuestionModal = () => {
    if (!communityBuzzOramTokenReady) {
      enqueueSnackbar('Upgrade to submit Community Buzz questions.', {
        variant: 'info',
      });
      return;
    }
    const { communityBuzzAddQuestionAction } = COMMUNITY_BUZZ_GA4_EVENTS;
    trackEventGA4({
      event_name: communityBuzzAddQuestionAction.name,
      type: communityBuzzAddQuestionAction.type,
    });
    setOpenQAEntryQuestionModal(true);
  };

  const handleCloseQAEntryQuestionModal = () => {
    setOpenQAEntryQuestionModal(false);
  };

  const handleOpenIntroModal = useCallback(() => {
    setIsIntroModalOpen(true);
    setIntroModalSeen();
  }, [setIntroModalSeen]);

  const handleCloseIntroModal = useCallback(() => {
    setIsIntroModalOpen(false);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (hasIntroModalBeenSeen()) {
      return;
    }

    const handleScrollPastSmartInsights = () => {
      if (hasIntroModalBeenSeen()) {
        return;
      }

      const smartInsightsSection = document.getElementById('smart-insights');

      if (!smartInsightsSection) {
        return;
      }

      if (smartInsightsSection.getBoundingClientRect().top < 0) {
        handleOpenIntroModal();
        window.removeEventListener('scroll', handleScrollPastSmartInsights);
      }
    };

    window.addEventListener('scroll', handleScrollPastSmartInsights);
    handleScrollPastSmartInsights();

    return () => {
      window.removeEventListener('scroll', handleScrollPastSmartInsights);
    };
  }, [handleOpenIntroModal, hasIntroModalBeenSeen]);

  const handleSubmitQuestion = async (questionParams: AnswerFormData) => {
    await createQuestionMutation.mutateAsync({
      body: questionParams.data,
      year: year,
      make: make,
      model: model,
      trim: trim,
      username: questionParams.name,
    });
  };

  const handleSubmitAnswer = async (answerParams: AnswerFormData) => {
    const questionId = selectedQuestion?.id;
    if (!questionId) {
      enqueueSnackbar('Select a question before posting an answer.', {
        variant: 'warning',
      });
      return;
    }

    await createAnswerMutation.mutateAsync({
      question_id: questionId,
      body: answerParams.data,
      username: answerParams.name,
    });
  };

  return (
    <ErrorBoundary
      trackEventFunction={trackEventGA4}
      logFunction={logError as any}
      section="community-buzz-section"
      fallback={
        <ReportSection
          dataCy="community-buzz"
          title="Community Buzz"
          hasContent={false}
          sectionId="community-buzz-section"
          customNullText="Something went wrong processing the community buzz section."
          titleSx={{
            fontSize: '1.5rem',
          }}
        />
      }
    >
      <ReportSection
        dataCy="community-buzz"
        title="Community Buzz"
        hasContent={true}
        sectionId="community-buzz-section"
        titleSx={{
          fontSize: '1.5rem',
        }}
      >
        <Stack spacing={2.5}>
          <CommunityBuzzIntroCTA make={make} model={model} year={year} />
          <Text variant="body2" color="text.secondary">
            Top questions about {make} {model} {year} {trim}'s performance
          </Text>
          <QuestionsListContent
            onOpenQADisplayModal={handleOpenQADisplayModal}
            setSelectedQuestion={setSelectedQuestion}
            questions={questions}
            isLoading={isQuestionsListLoading || isCommunityBuzzSeeding}
            isError={isQuestionsListError}
            currentPage={currentPage}
            pageSize={questionsFetchLimit}
            totalCount={questionsTotal}
            onPageChange={handlePageChange}
          />
          <Button
            variant="outlined"
            onClick={handleOpenQAEntryQuestionModal}
            fullWidth
            disabled={askQuestionDisabled}
            title={
              !communityBuzzOramTokenReady
                ? 'Upgrade to submit Community Buzz questions.'
                : undefined
            }
          >
            Ask a Question
          </Button>
        </Stack>
        <CommunityBuzzIntroModal
          open={isIntroModalOpen}
          onClose={handleCloseIntroModal}
          make={make}
          model={model}
          year={year}
          trim={trim}
        />
        <QADisplayModal
          open={openQADisplayModal}
          onClose={handleCloseQADisplayModal}
          question={questionData?.item}
          onSubmitAnswer={handleSubmitAnswer}
          isLoading={isQuestionDetailsLoading}
        />
        <QAEntryModal
          isOpen={openQAEntryQuestionModal}
          onClose={handleCloseQAEntryQuestionModal}
          onSubmit={handleSubmitQuestion}
          title="Write a Question"
          type="Question"
        />
      </ReportSection>
    </ErrorBoundary>
  );
};
